import type { VariationRateScope } from '@/types/api/variation-scope.types';

/**
 * Bulk orders — one vendor order spanning many sites in a project.
 *
 * Mirrors the read contract built in M2.2 (PE-613): `GET /bulk-orders`,
 * `GET /bulk-orders/counts` and `GET /bulk-orders/{id}`.
 */

/**
 * The ten statuses a bulk-order parent can hold.
 *
 * Context: the set splits in two and the split is load-bearing (ADR-B04, DL-30). The first
 * **seven** are lifecycle states the parent was transitioned into; the last three are states the
 * backend recomputes from the children. `Returned` is the terminal one: no live site order is left
 * and at least one ended `Returned` (DL-211). `Placing`, `Approving` and `Cancelling` each
 * front a multi-transaction fan-out and exist so an interrupted run is visible rather than
 * indistinguishable from a finished one — which is why a *stuck* parent in one of them is
 * something an owner must be able to filter for (DL-66).
 */
export const BULK_ORDER_STATUSES = [
  'Placing',
  'AwaitingApproval',
  'Approving',
  'Approved',
  'Rejected',
  'Cancelling',
  'Cancelled',
  'PartiallyDelivered',
  'Delivered',
  'Returned',
] as const;

export type BulkOrderStatus = (typeof BULK_ORDER_STATUSES)[number];

/**
One row of the bulk-orders list — the parent's own rollup, nothing from its children.
*/
export type BulkOrderListItem = {
  id: string;
  /**
  The human-facing `BLK…` identifier.
  */
  reference: string;
  projectId: string;
  /**
  Denormalised onto the parent at placement (PE-660), so the Project column needs no lookup.
  */
  projectName: string;
  vendorId: string;
  vendorName: string;
  status: BulkOrderStatus;
  /**
   * Whether an unresolved **gating** variation is holding this parent up. A row property rather
   * than a lifecycle state, so it is its own field and its own filter dimension — never a status
   * value. Always `false` until milestone B raises gating variations.
   */
  hasBlockingVariation: boolean;
  /**
   * A run on this parent has stopped, of any kind (PE-1038). A stopped delivery leaves the status
   * alone, so the row cannot tell it from the status. Absent from an API without it.
   */
  hasStoppedRun?: boolean;
  siteCount: number;
  total: number;
  /**
  `total` incl VAT, as served: the figure the list shows. Null while `Placing`, when the total is
  the agreed rates' value with no VAT on it yet.
  */
  totalInclVat: number | null;
  /**
  `total` when a decline or cancellation began, which zeroed it; null until one does.
  */
  cancellationTotalBefore: number | null;
  /**
  `cancellationTotalBefore` incl VAT: the figure an ended row shows. Null on a run that began
  before C.2, which saved none.
  */
  cancellationTotalBeforeInclVat: number | null;
  ownerId: string;
  ownerName: string;
  createdAt: string;
  version: number;
  /**
  The vendor has been sent the purchase order, by a share, an approval with notify or a manual mark
  (PE-1348). Absent from an API without it, which reads as not notified.
  */
  vendorNotified?: boolean;
};

/**
Parent detail for the hub (`GET /bulk-orders/{id}`).
*/
export type BulkOrder = {
  id: string;
  reference: string;
  projectId: string;
  projectName: string;
  organisationId: string;
  vendorId: string;
  vendorName: string;
  /**
  A payment-terms enum name (`Month_End`, `Thirty_Day`…), or `''` where the vendor has none (COD).
  */
  vendorAccountType: string;
  /**
  Set by a share to the vendor, an approval with notify, or a mark from the list (PE-1348).
  */
  vendorNotified: boolean;
  /**
   * The vendor's contact email, read live, or null where it has none (PE-1348). A share to the
   * vendor and the approve confirm's notify option are offered only with one (DL-263). Absent from
   * an API without it, which reads as none.
   */
  vendorContactEmail?: string | null;
  /**
   * Where the vendor's details are kept, read live with its email: `BOQPAY` for a synced Budget
   * Control vendor, `CUSTOM` for the org's own, null where the vendor cannot be read (DL-263).
   */
  vendorSource?: string | null;
  ownerId: string;
  ownerName: string;
  status: BulkOrderStatus;
  hasBlockingVariation: boolean;
  childOrderIds: string[];
  /**
   * One row per child order, read from the orders themselves (PE-702): every figure is the
   * server's, and a child whose site carries no allocation row still reports its real values.
   * Unpaged, bounded by the 100-site ceiling, sorted by site name. A parent still in `Placing`
   * lists the children placed so far; an abandoned one keeps them, as `Cancelled`.
   */
  children: BulkOrderChild[];
  perItemRate: BulkOrderItemRate[];
  allocations: BulkOrderAllocation[];
  /**
   * The order value and the site span, both SERVED rather than derived here EM the list row reports
   * the same two, from the same computation, so a row and the drawer opened from it cannot disagree.
   */
  total: number;
  /**
  `total` incl VAT, served beside it: what the hub shows, so it agrees with the list row's. Null
  while `Placing`, as on the list row.
  */
  totalInclVat: number | null;
  siteCount: number;
  /**
   * The parent's money, carried from its children (PE-959). Null until placement completes, and on
   * a parent placed before PE-959 shipped, which has never been computed.
   */
  amount: number | null;
  vat: number | null;
  amountWithVariations: number | null;
  vatWithVariations: number | null;
  /**
   * What the paid payment requests have paid, incl VAT, served at 2dp (PE-1168): the sum of every
   * linked site order's amount paid, whatever its status. Absent from an API without PE-1168, and
   * only ever shown, never summed or derived here.
   */
  amountPaid?: number;
  /**
  Completed credit and refund requests on the bulk order, incl VAT, served positive. Null, as is
  `netPaidInclVat`, when the viewer may not see its credits (`creditRequests` is null).
  */
  creditedInclVat?: number | null;
  /**
  `amountPaid` less `creditedInclVat`, worked out by the server.
  */
  netPaidInclVat?: number | null;
  /**
   * Every pending variation on a non-cancelled child, sorted by site then item (PE-969). `[]`, never
   * absent, when there are none.
   */
  variations: BulkOrderVariation[];
  /**
  One per denied variation, oldest first (PE-959). `[]` when there are none.
  */
  variationChangeNotices: BulkOrderVariationChangeNotice[];
  fanOutProgress: BulkOrderFanOutProgress;
  /**
   * An abandon has begun and not finished: still `Placing`, with some children cancelled. Derived
   * by the server on the read (PE-693). Abandon is one-way, so retry is refused from then on.
   */
  isAbandoning: boolean;
  /**
   * A fan-out run on this parent is still going, by the server's own stall rule (PE-971). A
   * stopped approval offers Retry only once this is false, so the threshold lives in one place.
   */
  isRunInProgress: boolean;
  /**
   * The run open on this parent, or `null` when none is (PE-1038; B.4 deferred it). It is what
   * tells a stopped delivery apart, since one leaves the parent `Approved` or `PartiallyDelivered`.
   * Absent from an API without it.
   */
  run?: BulkOrderRun | null;
  /**
  Completed bulk deliveries. A stopped one counts only once it finishes (PE-1038).
  */
  deliveryCount?: number;
  /**
  The parent has site orders and none is live: each is Cancelled, Rejected or Returned (DL-166).
  */
  noChildOrdersRemain: boolean;
  /**
   * The bulk order's payment requests and credits (PE-1180): `[]` before approval or when there are
   * none, and `null` only where the viewer may not see them, which the hub renders as nothing, not
   * as an empty list. Absent from an API without PE-1180, and read the same way. Served in the
   * order to render: a deposit first, then the payment request, then adjustments, oldest first.
   */
  paymentRequests?: BulkOrderPaymentRequest[] | null;
  creditRequests?: BulkOrderCreditRequest[] | null;
  /**
   * The decline or cancellation that claimed this parent (PE-1033): `null` until one does, and
   * absent from an API without it. Persisted at the claim, so it names who started it, never who
   * pressed Retry, and it is what both the stopped banner and the terminal banner read.
   */
  cancellation?: BulkOrderCancellation | null;
  version: number;
  createdAt: string;
};

/**
 * One payment request of the bulk order, as the read serves it (PE-1180).
 *
 * Context: `type` is served with spaces (`Deposit Request`, `Order Payment Request`,
 * `Adjustment Payment Request`) and a label keys on it, never on the number's prefix. Money is the
 * server's, at full precision and rounded to 2dp at the API; nothing here is summed or derived.
 */
export type BulkOrderPaymentRequest = {
  id: string;
  paymentRequestNumber: string;
  type: string;
  status: string;
  /**
  True only on a `Requested` request that holds a push back.
  */
  isPushedBack: boolean;
  amountInclVat: number;
};

/**
 * One bulk credit of the bulk order, as the read serves it (PE-1180). `receivedInclVat` is 0 until
 * the credit is `Complete`.
 */
export type BulkOrderCreditRequest = {
  id: string;
  requestNumber: string;
  type: 'Credit' | 'Refund';
  status: string;
  totals: { initialInclVat: number; receivedInclVat: number };
};

/**
What a decline or cancel ends the parent as.
*/
export type BulkOrderCancellationOutcome = 'Rejected' | 'Cancelled';

/**
The parent's persisted decline or cancellation (`GET /bulk-orders/{id}`, PE-1033).
*/
export type BulkOrderCancellation = {
  intendedOutcome: BulkOrderCancellationOutcome;
  reason: string;
  initiatedBy: { id: string; name: string };
  initiatedAt: string;
  /**
  `PaymentRequestDecline` when finance declining the bulk payment request cancelled it (PE-1040).
  */
  cause: 'Owner' | 'PaymentRequestDecline';
  paymentRequest: { id: string; number: string } | null;
  /**
  The bulk order's `total` when the run began; null on one begun before it was saved.
  */
  totalBefore: number | null;
  /**
  `totalBefore` incl VAT, the figure the hub shows for an ended bulk order; null on the same ones.
  */
  totalBeforeInclVat: number | null;
};

/**
 * `POST /bulk-orders/{id}/decline` and `/cancel`. A resume of a stopped run sends no reason: the
 * one saved at the claim stands (PE-1037).
 */
export type BulkOrderCancellationRequest = {
  version: number;
  reason?: string;
};

/**
One linked sub-order on the hub (PE-702).
*/
export type BulkOrderChild = {
  orderId: string;
  orderNumber: string;
  siteId: string;
  siteName: string;
  /**
  The child ORDER's own status (`Requested`, `Approved`, `Cancelled`…), not a bulk status.
  */
  status: string;
  itemCount: number;
  totalQuantity: number;
  amount: number;
  /**
  `amount` incl VAT, served beside it: what the hub's Total column shows.
  */
  amountInclVat: number;
  /**
  Whether a gating variation on this child is still unresolved (PE-969).
  */
  hasBlockingVariation: boolean;
};

export type BulkOrderVariationKind =
  | 'Rate'
  | 'Quantity'
  | 'RateAndQuantity'
  | 'Specification'
  | 'NewLine';

/**
 * One pending variation on a child, as the hub's variations list renders it (PE-969).
 *
 * Context: every figure is served. `amount` is the permanent movement and `thisOrderOnlyAmount` the
 * movement of re-pricing only this child (DL-170); the four inputs are what both arise from.
 */
export type BulkOrderVariation = {
  variationId: string;
  childOrderId: string;
  childOrderNumber: string;
  /**
  With `childOrderId`, what the order route's approve and deny take.
  */
  childOrderItemId: string;
  siteId: string;
  siteName: string;
  itemName: string;
  /**
  Null on a new line.
  */
  boqLineItemId: string | null;
  kind: BulkOrderVariationKind;
  isGating: boolean;
  /**
  `None` where nothing that moves money changes, as on a specification-only variation.
  */
  direction: 'Increase' | 'Decrease' | 'None';
  status: 'Pending';
  orderQuantity: number;
  outstandingQuantity: number;
  lineRate: number;
  orderRate: number;
  amount: number;
  thisOrderOnlyAmount: number | null;
  rateScope: VariationRateScope | null;
  /**
  The confirm offers the scope choice, and the approve must send one.
  */
  rateScopeApplies: boolean;
  reviewedBy: { id: string; name: string } | null;
  reviewedAt: string | null;
};

/**
What a denied variation removed from the bulk order (DL-155, PE-959).
*/
export type BulkOrderVariationChangeNotice = {
  variationId: string;
  childOrderId: string;
  childOrderNumber: string;
  siteId: string;
  siteName: string;
  itemName: string;
  quantityRemoved: number;
  /**
  Excl VAT.
  */
  amountRemoved: number;
  /**
  The denied line was the child's last, so the site's order was cancelled.
  */
  childCancelled: boolean;
  deniedBy: { id: string; name: string };
  deniedAt: string;
};

/**
A site whose date window approval extends to the approval day (FR-58, DL-185 A-16).
*/
export type BulkOrderDateRangeExtension = {
  siteId: string;
  siteName: string;
  childOrderId: string;
  childOrderNumber: string;
  evaluatedOn: string;
  fromStartDate: string;
  toStartDate: string;
  fromEndDate: string;
  toEndDate: string;
};

/**
A variation already resolved on its own site, recapped in the approval confirm.
*/
export type BulkOrderClearedVariation = {
  variationId: string;
  siteId: string;
  siteName: string;
  childOrderId: string;
  childOrderNumber: string;
  itemName: string;
  kind: BulkOrderVariationKind;
  oldRate: number;
  newRate: number;
  oldQuantity: number;
  newQuantity: number;
  rateScope: VariationRateScope | null;
  approvedBy: { id: string; name: string };
  approvedAt: string;
};

/**
 * What the approval confirm states (`GET /bulk-orders/{id}/approval-summary`, PE-963).
 *
 * Context: every figure, total and count is served; the confirm renders them and derives none.
 */
export type BulkOrderApprovalSummary = {
  bulkOrderId: string;
  version: number;
  status: BulkOrderStatus;
  childCount: number;
  noChildOrdersRemain: boolean;
  hasUnresolvedGatingVariation: boolean;
  gatingVariationCount: number;
  dateRangeExtensions: BulkOrderDateRangeExtension[];
  /**
  The detail's variation rows, the non-gating pending ones only: what approval clears.
  */
  reductions: BulkOrderVariation[];
  /**
  Excl VAT.
  */
  reductionTotals: { count: number; thisOrderOnly: number; permanent: number };
  /**
  The approve must carry a `reductionScope` exactly when this is true.
  */
  reductionScopeRequired: boolean;
  clearedVariations: BulkOrderClearedVariation[];
  removedItems: BulkOrderVariationChangeNotice[];
};

/**
`POST /bulk-orders/{id}/approve`. A resume omits the scope: the first attempt saved it.
*/
export type ApproveBulkOrderRequest = {
  version: number;
  reductionScope?: VariationRateScope;
  /** Owed when the approval moves a site's dates: the server refuses one without it (PE-1265). */
  siteDateChangeReason?: string;
  /**
   * Email the vendor the purchase order once approved (PE-1348). Saved with the run, so a resume
   * keeps the first attempt's choice and Retry approval sends none (DL-261 item 5).
   */
  notifyVendor?: boolean;
};

/**
`vendor` emails the vendor the PO and marks it notified; `self` emails the caller and writes nothing.
*/
export type BulkOrderShareTarget = 'vendor' | 'self';

export type ShareBulkOrderRequest = {
  target: BulkOrderShareTarget;
  version: number;
};

export type UpdateBulkOrderVendorNotifiedRequest = {
  vendorNotified: boolean;
  version: number;
};

export type BulkOrderItemRate = {
  itemId: string;
  rate: number;
  orderableQuantity: number;
};

export type BulkOrderAllocation = {
  siteId: string;
  itemId: string;
  quantity: number;
};

/**
 * One document the parent holds (`GET /bulk-orders/{id}/documents`, PE-886), with a signed link.
 *
 * Context: this rather than the detail's own `documents`, which carries the storage path and no
 * link. The set includes GRNs propagated UP from children (`sourceEntity: 'Order'`), and only a
 * document that originated on this parent can be deleted from it.
 */
export type BulkOrderSharedDocument = {
  id: string;
  name: string;
  url: string;
  urlExpiry: string;
  documentType: string;
  sourceEntity: string;
  sourceEntityId: string;
};

/**
 * Per-site progress through the parent's fan-out.
 *
 * Context: one record serves placement, cascade approval and cancellation, told apart by the
 * parent's status — there is no `PartiallyPlaced` status. A partial placement is a parent in
 * `Placing`; the sites not started are derived (`totalSites` less the other two), never returned.
 */
export type BulkOrderFanOutProgress = {
  totalSites: number;
  completedSiteIds: string[];
  failedSites: BulkOrderFanOutFailure[];
  lastAttemptAt: string | null;
  stoppedAt?: string | null;
};

export type BulkOrderRunKind =
  | 'Placement'
  | 'Approval'
  | 'Decline'
  | 'Cancellation'
  | 'Delivery'
  | 'Completion';

/**
 * The run open on a parent (`GET /bulk-orders/{id}`, PE-1038).
 *
 * Context: the server reads a `Running` marker silent for over two minutes as `Stopped`, so the
 * state is taken as served and never recomputed here.
 */
export type BulkOrderRun = {
  kind: BulkOrderRunKind;
  state: 'Running' | 'Stopped';
  /**
  A delivery's sites in run order, so the ones not started can be named; `null` for other kinds.
  */
  siteIds: string[] | null;
};

export type BulkOrderFanOutFailure = {
  siteId: string;
  /**
  Requested on PE-693 (2026-09-26) and not served on the detail yet, so the banner names the site by position.
  */
  siteName?: string;
  /**
  The server's words, rendered in full: it is often the thing someone has to go and fix.
  */
  reason: string;
  /**
  Accumulates across attempts, which is what marks a site that keeps failing.
  */
  attemptCount: number;
};

/**
One option for the tab's Project filter (`GET /bulk-orders/projects`, PE-660).
*/
export type BulkOrderProjectOption = {
  projectId: string;
  projectName: string;
};

/**
One option for the tab's Vendor filter (`GET /bulk-orders/vendors`, PE-977).
*/
export type BulkOrderVendorOption = {
  vendorId: string;
  vendorName: string;
};

/**
 * The filters shared by the list and the counts endpoint.
 *
 * Context: the counts endpoint deliberately cannot express `status` or `hasBlockingVariation`
 * (PE-613 / DL-54). Applying one tile's filter to the cross-tab would report zero for every other
 * tile, so the two dimensions the tiles drive live only on {@link BulkOrdersFilter}.
 */
export type BulkOrdersSharedFilter = {
  /**
  Search term for the `BLK…` reference. Partial and case-insensitive.
  */
  reference?: string;
  vendorId?: string;
  projectId?: string;
  startDate?: string;
  endDate?: string;
};

export type BulkOrdersFilter = BulkOrdersSharedFilter & {
  /**
  Zero or more statuses. An **array**, so one control serves a single status or a tile spanning several (DL-35).
  */
  status?: BulkOrderStatus[];
  /**
  Tri-state: `true`, `false`, or omitted. Omitted means "do not filter on it", which is not `false`.
  */
  hasBlockingVariation?: boolean;
};

export type BulkOrdersPagination = {
  pageNo?: number;
  pageSize?: number;
  orderBy?: string;
  orderByDesc?: string;
};

/**
 * One cell of the `(status, hasBlockingVariation)` cross-tab.
 *
 * Context: **a cell with no rows is absent, not zero** (PE-613). A consumer summing cells must
 * treat a missing cell as `0` rather than expecting all eighteen back.
 */
export type BulkOrderStatusFacetCell = {
  status: BulkOrderStatus;
  hasBlockingVariation: boolean;
  count: number;
};

export type BulkOrderStatusFacet = {
  cells: BulkOrderStatusFacetCell[];
};
